/**
 * Component Mapper
 *
 * Maps GeoEvent components to their Velocity equivalents.
 */

import type {
  GeoEventConfiguration,
  GeoEventComponent,
  Input,
  Output,
  ServiceNode,
  GeoEventService,
  TargetPlatform,
  MappingResult,
  MappingStatus,
  ConfigurationMappingResult,
  ComponentMapping,
  ParameterMapping,
  CompatibilityLevel,
  FunctionalMatrixEntry,
  LogicalCondition,
  AttributeCondition,
} from '../types/index.js';
import {
  getGeoEventComponentKind,
  isGeoEventServiceComponent,
  isInputComponent,
  isOutputComponent,
  isServiceNodeComponent,
} from '../types/index.js';

/**
 * Canonical Velocity display names for component slugs whose real product name
 * is NOT a plain title-case of the slug. Velocity analytic tools in particular
 * use verb-first names (e.g. the `field-mapper` slug is the "Map Fields" tool),
 * so a naive title-case produces a wrong, misleading label in the guide. Names
 * here match the Velocity documentation page titles (the matrix `velocityHelpUrl`
 * slugs), so the guide reads the same as the Velocity Manager UI. Slugs not
 * listed here fall back to acronym-aware title casing in {@link toHumanLabel}.
 */
const VELOCITY_DISPLAY_NAMES: Record<string, string> = {
  // Analytic tools (verb-first Velocity names differ from the slug word order)
  'field-mapper': 'Map Fields',
  'field-calculator': 'Calculate Field',
  'field-selector': 'Select Fields',
  'buffer-creator': 'Create Buffers',
  'incident-detector': 'Detect Incidents',
  'motion-calculator': 'Calculate Motion Statistics',
  'event-volume-controller': 'Control Event Volume',
  'range-fan': 'Create Range Fans',
  'distance-to-feature-calculator': 'Calculate Distance',
  'detect-gaps': 'Detect Gaps',
  'delay-and-reorder-tracks': 'Delay and Reorder Tracks',
  'reconstruct-tracks': 'Reconstruct Tracks',
  'join-features': 'Join Features',
  'feature-to-point': 'Feature to Point',
  'add-xyz-values': 'Add XYZ Values',
  'route-by-expressions': 'Route by Expressions',
  'filter-by-expression': 'Filter by Expression',
  'filter-by-geometry': 'Filter by Geometry',
  'filter-by-time-range': 'Filter by Time Range',
  'overlay': 'Overlay Layers',
  'split-by-attributes': 'Split by Attributes',
  'project': 'Project',
  'feature-layer-source': 'Feature Layer',
  // Feeds
  'feature-layer-feed': 'Feature Layer',
  'http-poller': 'HTTP Poller',
  'http-receiver-feed': 'HTTP Receiver',
  'websocket-feed': 'WebSocket',
  'rss-feed': 'RSS',
  'kafka-feed': 'Kafka',
  'mqtt-feed': 'MQTT',
  'tcp-feed': 'TCP',
  'watch-file': 'Watch File',
  'cloud-storage-feed': 'Cloud Storage',
  'gtfs': 'GTFS',
  'rabbitmq': 'RabbitMQ',
  'geotab-logrecord': 'Geotab Log Record',
  'flight-aware-airborne': 'FlightAware Airborne',
  'flight-aware-surface': 'FlightAware Surface',
  'verizon-reveal-2': 'Verizon Connect Reveal',
  // Outputs
  'write-file': 'Write to Local File',
  'feature-layer-new-output': 'Feature Layer (New)',
  'feature-layer-existing-output': 'Feature Layer (Existing)',
  'stream-layer-output': 'Stream Layer',
  'email-output': 'Email',
  'text-message-output': 'Text Message',
  'kafka-output': 'Kafka',
  'http-output': 'HTTP',
  'tcp': 'TCP',
  'rabbitmq-output': 'RabbitMQ',
  'azure-blob-output': 'Azure Blob Store',
};

/** Slug words that must be upper-cased (acronyms) in the title-case fallback. */
const ACRONYM_WORDS = new Set([
  'http', 'https', 'tcp', 'udp', 'mqtt', 'rss', 'gtfs', 'ais', 'sms', 'json',
  'xml', 'xyz', 'mgrs', 'url', 'api', 'iot', 'cot', 'cap', 'nmea', 'rap',
]);

/**
 * Converts a Velocity component slug to its human-readable display label.
 * Prefers the canonical Velocity product name from {@link VELOCITY_DISPLAY_NAMES};
 * otherwise falls back to acronym-aware Title Case.
 * e.g., "field-mapper" -> "Map Fields"; "feature-layer-feed" -> "Feature Layer Feed".
 */
function toHumanLabel(id: string): string {
  if (!id) return '';
  const canonical = VELOCITY_DISPLAY_NAMES[id];
  if (canonical) return canonical;
  return id
    .split('-')
    .map((word) =>
      ACRONYM_WORDS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

/**
 * Composes the public ArcGIS Online item URL for a GeoEvent Server Gallery
 * component when the matrix entry carries a `geoEventGalleryItemId`. The
 * matrix stores only the id; the renderer-facing URL is assembled here so a
 * future change to the canonical AGOL item URL only needs editing in one
 * place.
 */
function composeGalleryItemUrl(itemId?: string): string | undefined {
  if (!itemId) return undefined;
  return `https://www.arcgis.com/home/item.html?id=${itemId}`;
}

/**
 * Recursively determines whether a GeoEvent filter's condition tree uses the
 * regular-expression MATCHES operator. Velocity/Arcade has no native regex
 * support, so such filters are only partially compatible even though the
 * Velocity Filter tool exists.
 */
function filterConditionUsesRegex(condition: LogicalCondition): boolean {
  return condition.conditions.some((child) =>
    'operand' in child
      ? (child as AttributeCondition).operator === 'MATCHES'
      : filterConditionUsesRegex(child as LogicalCondition),
  );
}
import type { MigrationStrategy } from '../types/api.js';
import {
  LoadedFunctionalMatrix,
  lookupInputMapping,
  lookupOutputMapping,
  lookupProcessorMapping,
  lookupFilterMapping,
  getParameterLabel,
} from './matrix-loader.js';
import { assessGeoEventExpressionArcadeConversion } from './expression-converter.js';

/**
 * Parses parameter mappings from JSON string.
 *
 * The input string is produced by `matrix-loader.ts` via `JSON.stringify`
 * on already-validated data, so a parse failure here would indicate a
 * programmer error (e.g. someone bypassed the loader). We deliberately
 * let `JSON.parse` throw instead of swallowing the error and returning
 * `[]`, which previously hid matrix corruption behind silent "no
 * parameter mapping" output.
 */
function parseParameterMappings(json: string): ParameterMapping[] {
  return JSON.parse(json) as ParameterMapping[];
}

/**
 * Maps an input connector to Velocity
 */
function mapInput(
  input: Input,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform,
  _connections: import('../types/geoevent.js').ArcGISServerConnection[] = [],
  securedInputNames: Set<string> = new Set()
): MappingResult {
  const entry = lookupInputMapping(matrix, input.connector);

  if (!entry) {
    return {
      component: input,
      mapping: null,
      status: 'unmapped',
      userReviewRequired: true,
      explanation: `No Velocity equivalent found for input connector "${input.connectorLabel}". Manual configuration required.`,
    };
  }

  // Select platform-specific values when targeting Enterprise
  const isEnterprise = targetPlatform === 'velocity-enterprise';
  const velocityComponent = isEnterprise && entry.velocityEquivalentEnterprise
    ? entry.velocityEquivalentEnterprise
    : entry.velocityEquivalent;
  const velocityHelpUrl = isEnterprise && entry.velocityHelpUrlEnterprise
    ? entry.velocityHelpUrlEnterprise
    : entry.velocityHelpUrl;
  const compatNotes = isEnterprise && entry.compatibilityNotesEnterprise
    ? entry.compatibilityNotesEnterprise
    : entry.compatibilityNotes;
  const paramMappingsJson = isEnterprise && entry.parameterMappingsEnterprise
    ? entry.parameterMappingsEnterprise
    : entry.parameterMappings;

  const parameterMappings = parseParameterMappings(paramMappingsJson);
  // Use explicit compatibility field when available, fall back to inferring from notes
  const explicitCompat = isEnterprise && entry.compatibilityEnterprise
    ? entry.compatibilityEnterprise
    : entry.compatibility;
  let compatibility = explicitCompat || determineCompatibility(compatNotes);

  // TCP Server mode override: Enterprise supports TCP Client mode directly,
  // while Server mode requires manual review. Velocity Online has no TCP feed.
  const tcpMode = input.transport?.properties?.['mode']?.value;
  if (input.connector === 'esri-in-tcp-text-generictext' && tcpMode?.toUpperCase() === 'SERVER') {
    compatibility = targetPlatform === 'velocity-enterprise' ? 'partial' : 'none';
  }

  // Detect secured feature layer connections — these are not directly usable
  // in Velocity without additional configuration, so downgrade to partial
  const isSecured = securedInputNames.has(input.name);
  if (isSecured && compatibility === 'full') {
    compatibility = 'partial';
  }

  // Detect IncomingDataContainsGeoEventDefinition — when true, the input can
  // receive multiple schemas and the first field is a definition name
  // discriminator. Velocity does not support runtime schema selection, so
  // downgrade to partial and recommend reconfiguring in GeoEvent.
  const incomingContainsDef =
    input.adapter?.properties?.['IncomingDataContainsGeoEventDefinition']?.value === 'true';
  if (incomingContainsDef && compatibility === 'full') {
    compatibility = 'partial';
  }

  let notes = compatNotes;
  if (input.connector === 'esri-in-tcp-text-generictext' && tcpMode?.toUpperCase() === 'SERVER') {
    notes = 'TCP Server mode connections are not yet supported in Velocity but are on the roadmap. Only TCP Client mode is currently supported in Velocity for ArcGIS Enterprise.';
  }

  const limitations = extractLimitations(compatNotes);
  if (isSecured) {
    const registrationTarget = isEnterprise ? 'ArcGIS Portal' : 'ArcGIS Online';
    limitations.push(`Requires secured layer registration in ${registrationTarget}`);
  }
  if (incomingContainsDef) {
    limitations.push(
      'IncomingDataContainsGeoEventDefinition is enabled — Velocity does not support runtime schema selection. ' +
      'Consider disabling this setting in GeoEvent Server (set to false and assign a fixed GeoEvent Definition), ' +
      'then re-export and re-import. Alternatively, split this input into separate inputs per definition.'
    );
  }

  const mapping: ComponentMapping = {
    geoEventComponent: input.connector,
    geoEventComponentLabel: entry.geoEventConnectorLabel,
    geoEventType: 'input',
    velocityComponent,
    velocityComponentLabel: toHumanLabel(velocityComponent),
    velocityType: 'feed',
    compatibility,
    parameterMappings,
    alternatives: [],
    notes,
    limitations,
    migrationSteps: entry.migrationSteps,
    geoEventHelpUrl: entry.geoEventHelpUrl || undefined,
    velocityHelpUrl: velocityHelpUrl || undefined,
    geoEventGalleryItemUrl: composeGalleryItemUrl(entry.geoEventGalleryItemId),
    geoEventGalleryItemDeprecated: entry.geoEventGalleryItemDeprecated,
  };

  const status: MappingStatus = compatibility === 'none' ? 'unmapped' : compatibility === 'partial' ? 'partial' : 'mapped';

  return {
    component: input,
    mapping,
    status,
    userReviewRequired: compatibility !== 'full',
    explanation: generateExplanation(input.connectorLabel, velocityComponent, compatibility, notes),
  };
}

/**
 * Maps an output connector to Velocity
 */
function mapOutput(
  output: Output,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform,
  migrationStrategy: MigrationStrategy = 'side-by-side'
): MappingResult {
  const entry = lookupOutputMapping(matrix, output.connector);

  if (!entry) {
    return {
      component: output,
      mapping: null,
      status: 'unmapped',
      userReviewRequired: true,
      explanation: `No Velocity equivalent found for output connector "${output.connectorLabel}". Manual configuration required.`,
    };
  }

  const isEnterprise = targetPlatform === 'velocity-enterprise';
  const hasEnterpriseEquivalent = isEnterprise && Boolean(entry.velocityEquivalentEnterprise);
  const paramMappingsJson = hasEnterpriseEquivalent && entry.parameterMappingsEnterprise
    ? entry.parameterMappingsEnterprise
    : entry.parameterMappings;
  const parameterMappings = parseParameterMappings(paramMappingsJson);
  const compatNotes = hasEnterpriseEquivalent && entry.compatibilityNotesEnterprise
    ? entry.compatibilityNotesEnterprise
    : entry.compatibilityNotes;
  const explicitCompat = hasEnterpriseEquivalent && entry.compatibilityEnterprise
    ? entry.compatibilityEnterprise
    : entry.compatibility;
  const compatibility = explicitCompat || determineCompatibility(compatNotes);

  // Select the Velocity equivalent based on migration strategy
  const velocityComponent = hasEnterpriseEquivalent
    ? entry.velocityEquivalentEnterprise!
    : migrationStrategy === 'in-place' && entry.velocityEquivalentInPlace
    ? entry.velocityEquivalentInPlace
    : entry.velocityEquivalent;
  const velocityHelpUrl = hasEnterpriseEquivalent && entry.velocityHelpUrlEnterprise
    ? entry.velocityHelpUrlEnterprise
    : migrationStrategy === 'in-place' && entry.velocityHelpUrlInPlace
    ? entry.velocityHelpUrlInPlace
    : entry.velocityHelpUrl;
  const velocityLabel = toHumanLabel(velocityComponent);

  const mapping: ComponentMapping = {
    geoEventComponent: output.connector,
    geoEventComponentLabel: entry.geoEventConnectorLabel,
    geoEventType: 'output',
    velocityComponent,
    velocityComponentLabel: toHumanLabel(velocityComponent),
    velocityType: 'output',
    compatibility,
    parameterMappings,
    alternatives: [],
    notes: compatNotes,
    limitations: extractLimitations(compatNotes),
    migrationSteps: entry.migrationSteps,
    geoEventHelpUrl: entry.geoEventHelpUrl || undefined,
    velocityHelpUrl: velocityHelpUrl || undefined,
    geoEventGalleryItemUrl: composeGalleryItemUrl(entry.geoEventGalleryItemId),
    geoEventGalleryItemDeprecated: entry.geoEventGalleryItemDeprecated,
  };

  const status: MappingStatus = compatibility === 'none' ? 'unmapped' : compatibility === 'partial' ? 'partial' : 'mapped';

  return {
    component: output,
    mapping,
    status,
    userReviewRequired: compatibility !== 'full',
    explanation: generateExplanation(output.connectorLabel, velocityLabel, compatibility, compatNotes),
  };
}

/**
 * Maps a service node (filter or processor) to Velocity
 */
function resolveProcessorCompatibility(
  node: ServiceNode,
  entry: import('../types/mapping.js').FunctionalMatrixEntry
): { compatibility: CompatibilityLevel; notes: string; limitations: string[] } {
  let compatibility = entry.compatibility || determineCompatibility(entry.compatibilityNotes);
  let notes = entry.compatibilityNotes;
  let limitations = extractLimitations(entry.compatibilityNotes);

  if (entry.geoEventConnector === 'com.esri.ges.processor/FieldCalculator') {
    const expression = String(node.processor?.properties?.['expression']?.value || '');
    const assessment = assessGeoEventExpressionArcadeConversion(expression);

    if (assessment.autoConverted) {
      compatibility = 'full';
      notes = 'Calculate Field tool in Velocity provides equivalent functionality. The GeoEvent expression is covered by the ArcGIS Velocity Migration Tool Arcade conversion and is generated as a Velocity Arcade expression.';
      limitations = [];
    } else {
      compatibility = 'partial';
      if (assessment.reason) {
        notes = `${notes} Conversion note: ${assessment.reason}.`;
      }
    }
  }

  return { compatibility, notes, limitations };
}

function mapServiceNode(
  node: ServiceNode,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform
): MappingResult {
  // When targeting Velocity for ArcGIS Enterprise, prefer the Enterprise help topic
  // (doc.esri.com/en/arcgis-velocity/latest/...) over the Online topic.
  const isEnterprise = targetPlatform === 'velocity-enterprise';
  const resolveHelpUrl = (entry: FunctionalMatrixEntry): string | undefined =>
    (isEnterprise && entry.velocityHelpUrlEnterprise
      ? entry.velocityHelpUrlEnterprise
      : entry.velocityHelpUrl) || undefined;

  // Handle filter nodes
  if (node.filter) {
    const entry = lookupFilterMapping(matrix, 'attribute-filter');

    if (!entry) {
      return {
        component: node,
        mapping: null,
        status: 'unmapped',
        userReviewRequired: true,
        explanation: `No Velocity equivalent found for filter "${node.label}". Manual configuration required.`,
      };
    }

    const usesRegex = filterConditionUsesRegex(node.filter.logicalCondition);
    const regexNote =
      'This filter uses regular-expression pattern matching (the MATCHES operator). ' +
      'Velocity has no native regular-expression support — Filter by Expression evaluates ArcGIS Arcade only, ' +
      'and Arcade has no regex engine. The migration generates a best-effort Arcade text-function approximation ' +
      'for common, fixed-shape patterns and flags it for verification; patterns outside that set should be applied ' +
      'upstream before ingest or handled by an external service. Review and validate the generated filter expression before relying on it.';

    const mapping: ComponentMapping = {
      geoEventComponent: 'attribute-filter',
      geoEventComponentLabel: entry.geoEventConnectorLabel,
      geoEventType: 'filter',
      velocityComponent: entry.velocityEquivalent,
      velocityComponentLabel: toHumanLabel(entry.velocityEquivalent),
      velocityType: 'analytic-tool',
      compatibility: usesRegex ? 'partial' : 'full',
      parameterMappings: [],
      alternatives: [],
      notes: usesRegex ? `${entry.compatibilityNotes} ${regexNote}` : entry.compatibilityNotes,
      limitations: usesRegex
        ? ['Regular-expression (MATCHES) matching is not natively supported by Velocity or Arcade; the generated filter expression is a best-effort approximation that must be verified, or the pattern applied upstream/externally']
        : [],
      migrationSteps: entry.migrationSteps,
      geoEventHelpUrl: entry.geoEventHelpUrl || undefined,
      velocityHelpUrl: resolveHelpUrl(entry),
      geoEventGalleryItemUrl: composeGalleryItemUrl(entry.geoEventGalleryItemId),
      geoEventGalleryItemDeprecated: entry.geoEventGalleryItemDeprecated,
    };

    return {
      component: node,
      mapping,
      status: usesRegex ? 'partial' : 'mapped',
      userReviewRequired: usesRegex,
      explanation: usesRegex
        ? `Filter "${node.label}" maps to the Velocity Filter tool, but it uses regular-expression matching that Velocity and Arcade do not natively support; the generated Arcade approximation requires review.`
        : `Filter "${node.label}" maps to Velocity Filter tool.`,
    };
  }

  // Handle processor nodes
  if (node.processor) {
    const entry = lookupProcessorMapping(matrix, node.processor.uri);

    if (!entry) {
      return {
        component: node,
        mapping: null,
        status: 'unmapped',
        userReviewRequired: true,
        explanation: `No Velocity equivalent found for processor "${node.label}" (${node.processor.uri}). Manual configuration required.`,
      };
    }

    const parameterMappings = parseParameterMappings(entry.parameterMappings);
    const { compatibility, notes, limitations } = resolveProcessorCompatibility(node, entry);

    const mapping: ComponentMapping = {
      geoEventComponent: node.processor.uri,
      geoEventComponentLabel: entry.geoEventConnectorLabel,
      geoEventType: 'processor',
      velocityComponent: entry.velocityEquivalent,
      velocityComponentLabel: toHumanLabel(entry.velocityEquivalent),
      velocityType: 'analytic-tool',
      compatibility,
      parameterMappings,
      alternatives: [],
      notes,
      limitations,
      migrationSteps: entry.migrationSteps,
      geoEventHelpUrl: entry.geoEventHelpUrl || undefined,
      velocityHelpUrl: resolveHelpUrl(entry),
      geoEventGalleryItemUrl: composeGalleryItemUrl(entry.geoEventGalleryItemId),
      geoEventGalleryItemDeprecated: entry.geoEventGalleryItemDeprecated,
    };

    const status: MappingStatus = compatibility === 'none' ? 'unmapped' : compatibility === 'partial' ? 'partial' : 'mapped';

    return {
      component: node,
      mapping,
      status,
      userReviewRequired: compatibility !== 'full',
      explanation: generateExplanation(node.label, entry.velocityEquivalent, compatibility, notes),
    };
  }

  // Node with neither filter nor processor
  return {
    component: node,
    mapping: null,
    status: 'unmapped',
    userReviewRequired: true,
    explanation: `Service node "${node.label}" has no filter or processor definition.`,
  };
}

/**
 * Maps a GeoEvent service to Velocity analytics
 */
function mapGeoEventService(
  service: GeoEventService,
  _matrix: LoadedFunctionalMatrix,
  _targetPlatform: TargetPlatform
): MappingResult {
  // Services map to Velocity Real-Time Analytics
  const mapping: ComponentMapping = {
    geoEventComponent: service.name,
    geoEventComponentLabel: 'GeoEvent Service',
    geoEventType: 'processor',
    velocityComponent: 'real-time-analytic',
    velocityComponentLabel: 'Real-Time Analytic',
    velocityType: 'analytic',
    compatibility: 'full',
    parameterMappings: [],
    alternatives: [],
    notes: 'GeoEvent Services map to Velocity Real-Time Analytics.',
    limitations: [],
  };

  return {
    component: service,
    mapping,
    status: 'mapped',
    userReviewRequired: false,
    explanation: `GeoEvent Service "${service.label}" maps to a Velocity Real-Time Analytic.`,
  };
}

/**
 * Determines compatibility level from notes
 */
function determineCompatibility(notes: string): CompatibilityLevel {
  const lowerNotes = notes.toLowerCase();
  if (lowerNotes.includes('no direct') || lowerNotes.includes('not supported')) {
    return 'partial';
  }
  if (lowerNotes.includes('equivalent') || lowerNotes.includes('full')) {
    return 'full';
  }
  return 'partial';
}

/**
 * Extracts limitations from compatibility notes
 */
function extractLimitations(notes: string): string[] {
  const limitations: string[] = [];
  if (notes.includes('cloud')) {
    limitations.push('Requires cloud-accessible resources');
  }
  if (notes.includes('not supported')) {
    limitations.push('Some features not directly supported');
  }
  return limitations;
}

/**
 * Generates an explanation for a mapping
 */
function generateExplanation(
  sourceName: string,
  targetName: string,
  compatibility: CompatibilityLevel,
  notes: string
): string {
  switch (compatibility) {
    case 'full':
      return `"${sourceName}" maps directly to Velocity "${targetName}".`;
    case 'partial':
      return `"${sourceName}" partially maps to Velocity "${targetName}". ${notes}`;
    case 'none':
      return `"${sourceName}" has no Velocity equivalent. ${notes}`;
  }
}

/**
 * Maps a single GeoEvent component to Velocity
 */
export function mapComponent(
  component: GeoEventComponent,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform,
  migrationStrategy: MigrationStrategy = 'side-by-side',
  connections: import('../types/geoevent.js').ArcGISServerConnection[] = [],
  securedInputNames: Set<string> = new Set()
): MappingResult {
  const componentKind = getGeoEventComponentKind(component);

  if (componentKind === 'input' && isInputComponent(component)) {
    return mapInput(component, matrix, targetPlatform, connections, securedInputNames);
  }

  if (componentKind === 'output' && isOutputComponent(component)) {
    return mapOutput(component, matrix, targetPlatform, migrationStrategy);
  }

  if (componentKind === 'service-node' && isServiceNodeComponent(component)) {
    return mapServiceNode(component, matrix, targetPlatform);
  }

  if (componentKind === 'service' && isGeoEventServiceComponent(component)) {
    return mapGeoEventService(component, matrix, targetPlatform);
  }

  return {
    component,
    mapping: null,
    status: 'unmapped',
    userReviewRequired: true,
    explanation: 'Unable to determine component type for mapping.',
  };
}

/**
 * Maps an entire GeoEvent configuration to Velocity
 */
export function mapConfiguration(
  config: GeoEventConfiguration,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform,
  migrationStrategy: MigrationStrategy = 'side-by-side',
  securedInputNames: Set<string> = new Set()
): ConfigurationMappingResult {
  const mappings: MappingResult[] = [];
  const unmappedComponents: GeoEventComponent[] = [];
  const partialMappings: MappingResult[] = [];
  const reviewRequired: MappingResult[] = [];

  // Map inputs
  for (const input of config.inputs) {
    const result = mapComponent(input, matrix, targetPlatform, migrationStrategy, config.arcgisServerConnections, securedInputNames);
    mappings.push(result);

    if (result.status === 'unmapped') {
      unmappedComponents.push(input);
    } else if (result.status === 'partial') {
      partialMappings.push(result);
    }

    if (result.userReviewRequired) {
      reviewRequired.push(result);
    }
  }

  // Map outputs
  for (const output of config.outputs) {
    const result = mapComponent(output, matrix, targetPlatform, migrationStrategy, config.arcgisServerConnections, securedInputNames);
    mappings.push(result);

    if (result.status === 'unmapped') {
      unmappedComponents.push(output);
    } else if (result.status === 'partial') {
      partialMappings.push(result);
    }

    if (result.userReviewRequired) {
      reviewRequired.push(result);
    }
  }

  // Map services and their nodes
  for (const service of config.geoEventServices) {
    // Map the service itself
    const serviceResult = mapComponent(service, matrix, targetPlatform, migrationStrategy, config.arcgisServerConnections, securedInputNames);
    mappings.push(serviceResult);

    // Map nodes within the service
    for (const node of service.nodes) {
      const nodeResult = mapComponent(node, matrix, targetPlatform, migrationStrategy, config.arcgisServerConnections, securedInputNames);
      mappings.push(nodeResult);

      if (nodeResult.status === 'unmapped') {
        unmappedComponents.push(node);
      } else if (nodeResult.status === 'partial') {
        partialMappings.push(nodeResult);
      }

      if (nodeResult.userReviewRequired) {
        reviewRequired.push(nodeResult);
      }
    }
  }

  return {
    mappings,
    unmappedComponents,
    partialMappings,
    reviewRequired,
  };
}

/**
 * Gets a human-readable description for a component
 */
export function getHumanReadableDescription(
  component: GeoEventComponent,
  matrix: LoadedFunctionalMatrix
): string {
  if (isInputComponent(component)) {
    const input = component;
    const parts = [`Input: ${input.label || input.name}`];
    parts.push(`Connector: ${input.connectorLabel}`);

    // Add property descriptions
    for (const [key, prop] of Object.entries(input.transport.properties)) {
      const label = getParameterLabel(matrix, key);
      if (prop.value) {
        parts.push(`${label}: ${prop.value}`);
      }
    }

    return parts.join('\n');
  }

  if (isOutputComponent(component)) {
    const output = component;
    const parts = [`Output: ${output.label || output.name}`];
    parts.push(`Connector: ${output.connectorLabel}`);

    for (const [key, prop] of Object.entries(output.transport.properties)) {
      const label = getParameterLabel(matrix, key);
      if (prop.value) {
        parts.push(`${label}: ${prop.value}`);
      }
    }

    return parts.join('\n');
  }

  if (isServiceNodeComponent(component)) {
    const node = component;
    if (node.filter) {
      return `Filter: ${node.label}`;
    }
    if (node.processor) {
      const parts = [`Processor: ${node.label}`];
      for (const [key, prop] of Object.entries(node.processor.properties)) {
        const label = getParameterLabel(matrix, key);
        if (prop.value) {
          parts.push(`${label}: ${prop.value}`);
        }
      }
      return parts.join('\n');
    }
  }

  if (isGeoEventServiceComponent(component)) {
    const service = component;
    return `Service: ${service.label}\nDescription: ${service.description || 'No description'}`;
  }

  return 'Unknown component';
}
